/**
 * Decision Trail service — the client-side Trail (canon 27 §5).
 *
 * Assembles, for one item, everything that decided its fate: decisions
 * (direct back-link + related_objects multi-attach), governance events,
 * gated-reorder rank displacement, list publications whose snapshot contains
 * the item, value proposals/acceptances, rescores, and item lineage events —
 * plus plan-level Decisions (plan of record authorized; plan cycle activated)
 * for the plans that placed the item, which are attached to the plan, not the
 * item (see PLAN_DECISION_SOURCES, shared with the public endpoint below).
 *
 * This is the in-app twin of `GET /v1/items/:id/decision-lineage`
 * (supabase/functions/api/routes/items.ts) — same sources, same entry
 * semantics — plus session ranking facts derived from store data (rank,
 * composite and per-criterion scores, criteria weights, rank overrides).
 *
 * Every entry renders only what is in the store. A missing rationale is
 * reported as missing, never invented.
 */

import { formatGovernanceEventType } from './formatGovernanceEventDisplay';
import { getSafeSupabaseClient, getCurrentWorkspaceId } from './supabaseSync';
import { getTypedSupabaseClient } from './typedSupabaseClient';
import {
  PLAN_DECISION_SOURCES,
  isPlanDecisionSource,
  matchPlanDecisionsToItem,
  planPlacementsFrom,
  planVersionIdsOf,
} from '../../supabase/functions/_shared/planDecisionTrailCore';

// Plan-level Decision matching is shared with the public decision-lineage route.
export { PLAN_DECISION_SOURCES, matchPlanDecisionsToItem };
import type { SessionRankingOutput } from '../types/prioritization/session';

// ============================================================================
// Types
// ============================================================================

export type DecisionTrailEntryKind =
  | 'decision'
  | 'plan_commitment'
  | 'governance_event'
  | 'rank_displacement'
  | 'list_published'
  | 'value_proposed'
  | 'value_accepted'
  | 'rescore'
  | 'item_lineage'
  | 'recorded_rationale';

export interface DecisionTrailEntry {
  kind: DecisionTrailEntryKind;
  /** ISO timestamp of the moment; null when the source row has none. */
  at: string | null;
  title: string;
  actorId: string | null;
  actorName: string | null;
  /** The recorded rationale/note. Null means none was recorded — render that honestly. */
  why: string | null;
  /** Memorialized Decision this entry is (or is an effect of), when one exists. */
  decisionId: string | null;
  sessionId?: string | null;
  sourceTable: string;
  sourceId: string;
  detail?: Record<string, unknown>;
}

type Row = Record<string, unknown>;

export interface DecisionTrailSourceRows {
  decisionsDirect: Row[];
  decisionsAttached: Row[];
  governanceEvents: Row[];
  /** governance_events whose before/after priority_ids snapshot contains the item. */
  snapshotEvents: Row[];
  valueProposals: Row[];
  rescores: Row[];
  lineageEvents: Row[];
  /** Deliberation Record (Doc 21 §6.4): per-item rationale notes recorded in sessions. */
  deliberationAnnotations?: Row[];
  /** Deliberation Record: facilitator override reasons recorded in sessions. */
  deliberationOverrides?: Row[];
  /**
   * Plan-level Decisions (sources in PLAN_DECISION_SOURCES). They are attached
   * to the plan cycle / version, not to items, so they are matched to this
   * item through `planPlacements` + `planVersions` below.
   */
  planDecisions?: Row[];
  /**
   * Rows placing this item in a plan version: plan_records (planning_priority_id
   * = the item, above or below the line) and initiatives (item_id = the item,
   * included). Each carries `plan_version_id`; plan_records also `decision`.
   */
  planPlacements?: Row[];
  /** plan_versions rows (`id`, `plan_cycle_id`) for the versions above. */
  planVersions?: Row[];
}

// ============================================================================
// Pure assembly (unit-tested)
// ============================================================================

function str(v: unknown): string | null {
  return typeof v === 'string' && v.length > 0 ? v : null;
}

function snapshotPriorityIds(snapshot: unknown): string[] {
  if (!snapshot || typeof snapshot !== 'object') return [];
  const ids = (snapshot as Row).priority_ids;
  return Array.isArray(ids) ? ids.filter((x): x is string => typeof x === 'string') : [];
}

/** Position (1-based) of the item in a snapshot's priority_ids, or null. */
export function snapshotPosition(snapshot: unknown, itemId: string): number | null {
  const idx = snapshotPriorityIds(snapshot).indexOf(itemId);
  return idx >= 0 ? idx + 1 : null;
}

/**
 * Normalize the raw source rows into a single newest-first trail.
 * Decisions found via both the direct back-link and related_objects are
 * deduped by id; snapshot events are turned into rank-displacement /
 * list-publication entries with positions computed for this item.
 */
export function assembleDecisionTrail(
  itemId: string,
  rows: DecisionTrailSourceRows
): DecisionTrailEntry[] {
  const entries: DecisionTrailEntry[] = [];

  const decisionRows = new Map<string, Row>();
  for (const d of [...rows.decisionsDirect, ...rows.decisionsAttached]) {
    if (typeof d.id === 'string') decisionRows.set(d.id, d);
  }
  // Plan-level Decisions reach the item through the plan that placed it.
  const planMatch = new Map<string, { planVersionId: string | null; placement: string | null }>();
  for (const m of matchPlanDecisionsToItem(rows.planDecisions ?? [], rows.planPlacements ?? [], rows.planVersions ?? [])) {
    const id = m.decision.id;
    if (typeof id !== 'string') continue;
    if (!decisionRows.has(id)) decisionRows.set(id, m.decision);
    planMatch.set(id, { planVersionId: m.planVersionId, placement: m.placement });
  }
  for (const d of decisionRows.values()) {
    const plan = planMatch.get(d.id as string);
    entries.push({
      kind: isPlanDecisionSource(d.source) ? 'plan_commitment' : 'decision',
      at: str(d.decided_at) ?? str(d.created_at),
      title: str(d.title) ?? 'Decision',
      actorId: str(d.decided_by),
      actorName: str(d.decided_by_name),
      why: str(d.body) ?? str(d.rationale_reasoning),
      decisionId: d.id as string,
      sessionId: str(d.session_id),
      sourceTable: 'decisions',
      sourceId: d.id as string,
      detail: {
        subjectKind: d.subject_kind ?? null,
        decisionClass: d.decision_class ?? null,
        decisionKind: d.decision_kind ?? null,
        status: d.status ?? null,
        site: d.site ?? null,
        impact: d.impact ?? null,
        supersedesDecisionId: d.supersedes_decision_id ?? null,
        ...(plan ? { planVersionId: plan.planVersionId, planPlacement: plan.placement } : {}),
      },
    });
  }

  // Governance events directly on the item (skip ones the snapshot pass covers).
  const snapshotEventIds = new Set(rows.snapshotEvents.map((g) => g.id as string));
  for (const g of rows.governanceEvents) {
    if (snapshotEventIds.has(g.id as string)) continue;
    entries.push({
      kind: 'governance_event',
      at: str(g.occurred_at),
      title: g.event_type ? formatGovernanceEventType(String(g.event_type)) : 'Governance event',
      actorId: str(g.actor_id),
      actorName: str(g.actor_name),
      why: str(g.rationale) ?? str(g.note),
      decisionId: str(g.decision_id),
      sessionId: str(g.session_id),
      sourceTable: 'governance_events',
      sourceId: g.id as string,
    });
  }

  // Snapshot events: gated reorders → displacement; publications → entry rank.
  const seenSnapshot = new Set<string>();
  for (const g of rows.snapshotEvents) {
    const id = g.id as string;
    if (seenSnapshot.has(id)) continue;
    seenSnapshot.add(id);

    const fromPos = snapshotPosition(g.before_state_snapshot, itemId);
    const toPos = snapshotPosition(g.after_state_snapshot, itemId);
    const after = (g.after_state_snapshot ?? null) as Row | null;
    const before = (g.before_state_snapshot ?? null) as Row | null;
    const poolId = str(after?.pool_id) ?? str(before?.pool_id);
    const poolName = str(after?.pool_name) ?? str(before?.pool_name);

    if (g.event_type === 'pool_published') {
      entries.push({
        kind: 'list_published',
        at: str(g.occurred_at),
        title: toPos !== null
          ? `Published at #${toPos}${poolName ? ` in "${poolName}"` : ''}`
          : `List published${poolName ? `: "${poolName}"` : ''}`,
        actorId: str(g.actor_id),
        actorName: str(g.actor_name),
        why: str(g.rationale) ?? str(g.note),
        decisionId: str(g.decision_id),
        sourceTable: 'governance_events',
        sourceId: id,
        detail: { position: toPos, poolId, poolName, version: after?.version ?? null },
      });
    } else {
      entries.push({
        kind: 'rank_displacement',
        at: str(g.occurred_at),
        title:
          fromPos !== null && toPos !== null && fromPos !== toPos
            ? `Reordered: #${fromPos} → #${toPos}`
            : fromPos !== null && fromPos === toPos
              ? `Held position #${fromPos} through a reorder`
              : 'Affected by a published reorder',
        actorId: str(g.actor_id),
        actorName: str(g.actor_name),
        why: str(g.rationale),
        decisionId: str(g.decision_id),
        sourceTable: 'governance_events',
        sourceId: id,
        detail: { fromPosition: fromPos, toPosition: toPos, poolId },
      });
    }
  }

  // Valuations — the proposal and its acceptance are separate acts.
  for (const p of rows.valueProposals) {
    entries.push({
      kind: 'value_proposed',
      at: str(p.proposed_at),
      title: `Value proposed: ${str(p.attribute_key) ?? '?'}`,
      actorId: null,
      actorName: str(p.proposed_by_name) ?? str(p.proposed_by),
      why: str(p.rationale),
      decisionId: null,
      sessionId: str(p.session_id),
      sourceTable: 'value_proposals',
      sourceId: p.id as string,
      detail: { value: p.value, status: p.status },
    });
    if (p.status === 'accepted' && p.accepted_at) {
      entries.push({
        kind: 'value_accepted',
        at: str(p.accepted_at),
        title: `Value accepted: ${str(p.attribute_key) ?? '?'}`,
        actorId: null,
        actorName: str(p.accepted_by),
        why: str(p.rationale),
        decisionId: null,
        sessionId: str(p.session_id),
        sourceTable: 'value_proposals',
        sourceId: p.id as string,
        detail: { value: p.value },
      });
    }
  }

  for (const r of rows.rescores) {
    entries.push({
      kind: 'rescore',
      at: str(r.created_at),
      title: `Rescored: ${str(r.attribute_key) ?? '?'} ${r.previous_score ?? '—'} → ${r.revised_score ?? '—'}`,
      actorId: null,
      actorName: str(r.created_by),
      why: str(r.rationale),
      decisionId: null,
      sourceTable: 'rescoring_events',
      sourceId: r.id as string,
      detail: { triggerType: r.trigger_type ?? null, revisitationRequired: r.revisitation_required ?? null },
    });
  }

  for (const l of rows.lineageEvents) {
    entries.push({
      kind: 'item_lineage',
      at: str(l.created_at),
      title: `Item ${str(l.event_type) ?? 'lineage event'}`,
      actorId: str(l.performed_by),
      actorName: null,
      why: str(l.rationale),
      decisionId: null,
      sourceTable: 'lineage_events',
      sourceId: l.id as string,
    });
  }

  entries.sort((a, b) => (b.at ?? '').localeCompare(a.at ?? ''));
  // Recorded rationale — the human "why" from the Deliberation Record.
  // Rendered verbatim with attribution; when there is none, the panel says so.
  for (const a of rows.deliberationAnnotations ?? []) {
    // A note its author retracted (mig 746) is kept in the record, not shown as rationale.
    if (a.item_id !== itemId || a.retracted_at) continue;
    entries.push({
      kind: 'recorded_rationale',
      at: str(a.created_at),
      title: a.source === 'transcript_excerpt' ? 'Rationale (from transcript)' : 'Rationale recorded',
      actorId: str(a.author_id),
      actorName: null,
      why: str(a.content),
      decisionId: null,
      sessionId: str(a.session_id),
      sourceTable: 'deliberation_item_annotations',
      sourceId: a.id as string,
      detail: { source: a.source ?? 'manual', transcriptTimestamp: a.transcript_timestamp ?? null },
    });
  }
  for (const o of rows.deliberationOverrides ?? []) {
    if (o.item_id !== itemId) continue;
    entries.push({
      kind: 'recorded_rationale',
      at: str(o.created_at),
      title: 'Facilitator override — reason',
      actorId: str(o.overridden_by),
      actorName: null,
      why: str(o.reason),
      decisionId: null,
      sessionId: str(o.session_id),
      sourceTable: 'deliberation_override_justifications',
      sourceId: o.id as string,
      detail: { aggregateResult: o.aggregate_result ?? null, overrideResult: o.override_result ?? null },
    });
  }

  return entries;
}

// ============================================================================
// Session ranking facts (derived from store data, not fetched here)
// ============================================================================

export interface SessionRankingFact {
  sessionId: string;
  sessionName: string;
  state: string | null;
  rank: number | null;
  of: number;
  /** Composite score for the item (the `total` key of the per-criterion record). */
  totalScore: number | null;
  scoresByCriterion: Array<{ key: string; score: number; weight: number | null }>;
  toolName: string | null;
  overrides: Array<{
    fromRank: number;
    toRank: number;
    rationale: string;
    overriddenBy: string;
    overriddenAt: Date | null;
  }>;
}

interface SessionLike {
  id: string;
  name?: string | null;
  state?: string | null;
  itemIds?: string[] | null;
  rankingOutput?: SessionRankingOutput | null;
  criteriaConfig?: { criteriaIds?: string[]; weights?: Record<string, number> } | null;
}

/**
 * For every session whose ranking output includes the item: its rank, scores
 * (composite + per criterion, with the criterion weight in effect), and any
 * rank overrides that touched it — each of which carries a required rationale.
 */
export function deriveSessionRankingFacts(
  itemId: string,
  sessions: SessionLike[]
): SessionRankingFact[] {
  const facts: SessionRankingFact[] = [];

  for (const session of sessions) {
    const ro = session.rankingOutput;
    if (!ro?.rankedItemIds?.length) continue;
    const idx = ro.rankedItemIds.indexOf(itemId);
    if (idx < 0) continue;

    const scoreRecord = ro.scores?.[itemId] ?? {};
    const weights = session.criteriaConfig?.weights ?? {};
    const scoresByCriterion = Object.entries(scoreRecord)
      .filter(([key, value]) => key !== 'total' && typeof value === 'number')
      .map(([key, score]) => ({
        key,
        score,
        weight: typeof weights[key] === 'number' ? weights[key] : null,
      }));

    const overrides = (ro.overrides ?? [])
      .filter((o) => o.itemId === itemId)
      .map((o) => ({
        fromRank: o.originalRank,
        toRank: o.newRank,
        rationale: o.rationale,
        overriddenBy: o.overriddenBy,
        overriddenAt: o.overriddenAt ? new Date(o.overriddenAt) : null,
      }));

    facts.push({
      sessionId: session.id,
      sessionName: session.name || 'Untitled session',
      state: session.state ?? null,
      rank: idx + 1,
      of: ro.rankedItemIds.length,
      totalScore: typeof scoreRecord.total === 'number' ? scoreRecord.total : null,
      scoresByCriterion,
      toolName: ro.toolName || null,
      overrides,
    });
  }

  return facts;
}

// ============================================================================
// Scores by criterion (criterion_scores_by_session, mig 842)
// ============================================================================

/** Who produced a set of per-criterion scores in a session. */
export type CriterionScoreSource = 'group' | 'participants' | 'facilitator';

/** One criterion's scores for the item in one session, from one source. */
export interface CriterionScore {
  key: string;
  label: string;
  weight: number | null;
  scorerCount: number;
  mean: number;
  min: number;
  max: number;
}

/** A session's per-criterion scores for the item, grouped by who scored. */
export interface SessionCriterionScores {
  sessionId: string;
  sessionName: string;
  sources: Array<{ source: CriterionScoreSource; scorerCount: number; scores: CriterionScore[] }>;
}

const SOURCE_ORDER: CriterionScoreSource[] = ['group', 'participants', 'facilitator'];

function num(v: unknown): number | null {
  if (typeof v === 'number' && Number.isFinite(v)) return v;
  if (typeof v === 'string' && v.trim() !== '' && Number.isFinite(Number(v))) return Number(v);
  return null;
}

/**
 * Group the RPC's flat rows (one per session × item × criterion × source)
 * into one entry per session, sources in a fixed order. Rows with an unknown
 * source or no mean are skipped rather than guessed at.
 */
export function groupCriterionScores(rows: readonly Row[]): SessionCriterionScores[] {
  const bySession = new Map<string, SessionCriterionScores>();
  for (const r of rows) {
    const sessionId = str(r.session_id);
    const source = r.source as CriterionScoreSource;
    const mean = num(r.mean_score);
    const key = str(r.criterion_key);
    if (!sessionId || !key || mean === null || !SOURCE_ORDER.includes(source)) continue;
    let session = bySession.get(sessionId);
    if (!session) {
      session = { sessionId, sessionName: str(r.session_name) ?? 'Untitled session', sources: [] };
      bySession.set(sessionId, session);
    }
    let bucket = session.sources.find((s) => s.source === source);
    if (!bucket) {
      bucket = { source, scorerCount: 0, scores: [] };
      session.sources.push(bucket);
    }
    const scorerCount = num(r.scorer_count) ?? 0;
    bucket.scorerCount = Math.max(bucket.scorerCount, scorerCount);
    bucket.scores.push({
      key,
      label: str(r.criterion_label) ?? key,
      weight: num(r.weight),
      scorerCount,
      mean,
      min: num(r.min_score) ?? mean,
      max: num(r.max_score) ?? mean,
    });
  }
  for (const session of bySession.values()) {
    session.sources.sort((a, b) => SOURCE_ORDER.indexOf(a.source) - SOURCE_ORDER.indexOf(b.source));
  }
  return [...bySession.values()];
}

/**
 * The item's scores by criterion in every session that scored it, from
 * group scoring, participants' scoring tools and the facilitator engine.
 * Group and participant scores appear once that session's scoring is over.
 */
export async function fetchCriterionScores(itemId: string): Promise<{
  sessions: SessionCriterionScores[];
  error: string | null;
}> {
  const supabase = getSafeSupabaseClient();
  if (!supabase) return { sessions: [], error: 'Database not available' };
  // Untyped call: criterion_scores_by_session (mig 842) is not yet in the
  // generated types, which are generated from production.
  const loose = supabase as unknown as {
    rpc: (fn: string, args: Record<string, unknown>) => PromiseLike<{ data: Row[] | null; error: { message: string } | null }>;
  };
  const { data, error } = await loose.rpc('criterion_scores_by_session', { p_item_id: itemId });
  if (error) return { sessions: [], error: error.message };
  return { sessions: groupCriterionScores(data ?? []), error: null };
}

// ============================================================================
// Fetch
// ============================================================================

/**
 * The rows that tie plan-level Decisions to this item. Plan authorization
 * Decisions are attached to the plan (mig 788), never to items, so the item's
 * plans are found first: plan_records whose planning_priority_id is the item
 * (the engine's priority grain uses the item id — see mig 764), plus
 * initiatives for the item (the included commitments). Their versions give the
 * plan cycles. Only when the item is in some plan are the plan Decisions read.
 */
async function fetchPlanDecisionRows(itemId: string, workspaceId: string | null): Promise<{
  planDecisions: Row[];
  planPlacements: Row[];
  planVersions: Row[];
  error: string | null;
}> {
  const db = getTypedSupabaseClient();
  const [records, initiatives] = await Promise.all([
    db.from('plan_records').select('plan_version_id, decision').eq('planning_priority_id', itemId),
    db.from('initiatives').select('plan_version_id').eq('item_id', itemId),
  ]);
  const planPlacements = planPlacementsFrom(records.data ?? [], initiatives.data ?? []);
  const versionIds = planVersionIdsOf(planPlacements);
  const firstError = records.error?.message ?? initiatives.error?.message ?? null;
  if (versionIds.length === 0) {
    return { planDecisions: [], planPlacements, planVersions: [], error: firstError };
  }

  let decisionsQuery = db.from('decisions').select('*').in('source', Object.keys(PLAN_DECISION_SOURCES));
  if (workspaceId) decisionsQuery = decisionsQuery.eq('workspace_id', workspaceId);
  const [versions, decisions] = await Promise.all([
    db.from('plan_versions').select('id, plan_cycle_id').in('id', versionIds),
    decisionsQuery,
  ]);
  return {
    planDecisions: (decisions.data ?? []) as Row[],
    planPlacements,
    planVersions: (versions.data ?? []) as Row[],
    error: firstError ?? versions.error?.message ?? decisions.error?.message ?? null,
  };
}

/**
 * Fetch and assemble the decision trail for one item. Reads the same durable
 * sources as the public decision-lineage endpoint; RLS scopes every query,
 * with an explicit workspace filter added where the table carries one.
 */
export async function fetchDecisionTrail(itemId: string): Promise<{
  entries: DecisionTrailEntry[];
  error: string | null;
}> {
  const supabase = getSafeSupabaseClient();
  if (!supabase) {
    return { entries: [], error: 'Database not available' };
  }
  const workspaceId = getCurrentWorkspaceId();
  const withWorkspace = <T extends { eq: (col: string, v: string) => T }>(q: T): T =>
    workspaceId ? q.eq('workspace_id', workspaceId) : q;

  const relatedObjectFilter = JSON.stringify([{ kind: 'item', id: itemId }]);

  const [
    decisionsDirect,
    decisionsAttached,
    governanceEvents,
    snapshotAfter,
    snapshotBefore,
    valueProposals,
    rescores,
    lineageEvents,
  ] = await Promise.all([
    withWorkspace(supabase.from('decisions').select('*')).eq('item_id', itemId),
    withWorkspace(supabase.from('decisions').select('*')).contains('related_objects', relatedObjectFilter),
    withWorkspace(supabase.from('governance_events').select('*')).eq('item_id', itemId),
    withWorkspace(
      supabase.from('governance_events').select('*').in('event_type', ['priority_reordered', 'pool_published'])
    ).contains('after_state_snapshot', { priority_ids: [itemId] }),
    withWorkspace(
      supabase.from('governance_events').select('*').eq('event_type', 'priority_reordered')
    ).contains('before_state_snapshot', { priority_ids: [itemId] }),
    supabase.from('value_proposals').select('*').eq('item_id', itemId),
    withWorkspace(supabase.from('rescoring_events').select('*')).eq('item_id', itemId),
    supabase
      .from('lineage_events')
      .select('*')
      .or(`source_item_ids.cs.{${itemId}},target_item_ids.cs.{${itemId}}`),
  ]);
  // Deliberation Record reads (Brief K slice 1) — a second batch keeps the
  // first Promise.all's tuple type within the compiler's depth limit.
  // Untyped handle: the generated schema is large enough that a typed `.eq`
  // here trips TS2589; the rows are consumed as Row (Record<string, unknown>) anyway.
  type LooseQuery = PromiseLike<{ data: Row[] | null; error: { message: string } | null }>;
  type LooseFilter = LooseQuery & { eq: (col: string, v: string) => LooseFilter };
  const loose = supabase as unknown as { from: (table: string) => { select: (cols: string) => { eq: (col: string, v: string) => LooseFilter } } };
  const [deliberationAnnotations, deliberationOverrides, plan] = await Promise.all([
    // Confirmed notes only (mig 831): an unreviewed transcript proposal is not part of the trail.
    loose.from('deliberation_item_annotations').select('*').eq('item_id', itemId).eq('status', 'confirmed'),
    loose.from('deliberation_override_justifications').select('*').eq('item_id', itemId),
    fetchPlanDecisionRows(itemId, workspaceId),
  ]);

  const results = [
    decisionsDirect, decisionsAttached, governanceEvents,
    snapshotAfter, snapshotBefore, valueProposals, rescores, lineageEvents,
    deliberationAnnotations, deliberationOverrides,
  ];
  const firstError = results.find((r) => r.error)?.error?.message ?? plan.error;

  const entries = assembleDecisionTrail(itemId, {
    decisionsDirect: (decisionsDirect.data ?? []) as Row[],
    decisionsAttached: (decisionsAttached.data ?? []) as Row[],
    governanceEvents: (governanceEvents.data ?? []) as Row[],
    snapshotEvents: [
      ...((snapshotAfter.data ?? []) as Row[]),
      ...((snapshotBefore.data ?? []) as Row[]),
    ],
    valueProposals: (valueProposals.data ?? []) as Row[],
    rescores: (rescores.data ?? []) as Row[],
    lineageEvents: (lineageEvents.data ?? []) as Row[],
    deliberationAnnotations: (deliberationAnnotations.data ?? []) as Row[],
    deliberationOverrides: (deliberationOverrides.data ?? []) as Row[],
    planDecisions: plan.planDecisions,
    planPlacements: plan.planPlacements,
    planVersions: plan.planVersions,
  });

  return { entries, error: firstError };
}
